<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t(`dialog.dataCalculator.title`)"
    :is-valid="selectedAttributes.length > 0"
    agree-text="dialog.agreeFill"
    :loading="isDialogLoading"
    @agree="fillAttributes"
  >
    <template #body>
      <v-row no-gutters>
        <v-col v-if="isFetched">
          <v-row no-gutters align="baseline">
            <v-col>
              <v-alert
                icon="mdi-information-outline"
                color="primary"
                style="font-size: 12px"
                class="text-left"
                dense
                text
              >
                {{ $i18n.t('dialog.dataCalculator.info') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row
            v-for="attribute in [
              { name: 'checkAll', verbose_name: $i18n.t('dialog.dataCalculator.checkAll') },
              ...attributes,
            ]"
            :key="attribute.name"
            style="width: 100%"
            no-gutters
          >
            <v-col>
              <v-checkbox
                :hide-details="true"
                :input-value="
                  attribute.name === 'checkAll'
                    ? isCheckAllChecked
                    : selectedAttributes.find(attr => attr.id === attribute.id)
                "
                @change="e => (attribute.name === 'checkAll' ? onCheckAllChanged(e) : checkAttribute(e, attribute))"
              >
                <template #label>
                  <v-row no-gutters align="center" style="column-gap: 0.4rem">
                    <v-col cols="auto">
                      <span class="text-body-2" style="color: black">{{ attribute.verbose_name }}</span>
                    </v-col>
                    <v-col cols="auto">
                      <italic-subheader
                        v-if="attribute.driver_verbose_name"
                        :straight-text="`(${attribute.driver_verbose_name})`"
                      ></italic-subheader>
                    </v-col>
                  </v-row>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-else>
          <main-loader />
        </v-col>
      </v-row>
    </template>
  </main-large-dialog>
</template>

<script>
import { get, call } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import ItalicSubheader from '@/components/ItalicSubheader';

export default {
  name: 'DataCalculatorDialog',
  components: {
    MainLoader,
    ItalicSubheader,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  props: {
    isLoading: {
      type: Boolean,
    },
    isVisible: {
      type: Boolean,
    },
  },
  data: () => ({
    isFetched: false,
    selectedAttributes: [],
    attributes: [],
    currentTaskId: null,
    isDialogLoading: false,
  }),
  computed: {
    configurations: get('dataCalculator/configurations'),
    drivers: get('dataCalculator/drivers'),
    currentDataSource: get('layers/currentLayer@data_source_name'),
    currentLayerId: get('layers/currentLayer@id'),
    currentLayerSelectedFeatures: get('map/selectedFeatures@:currentLayerId'),
    dataSources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    isCheckAllChecked() {
      return this.attributes.length === this.selectedAttributes.length;
    },
    currentDataSourceConfiguration() {
      return this.configurations.filter(config => config.datasource === this.currentDataSource);
    },
  },
  watch: {
    isVisible(nV) {
      if (!nV) this.selectedAttributes = [];
    },
  },
  methods: {
    getDataCalculatorDrivers: call('dataCalculator/getDataCalculatorDrivers'),
    dataCalculatorCalculate: call('dataCalculator/dataCalculatorCalculate'),
    stopCeleryTask: call('celeryTasks/stopCeleryTask'),
    onCheckAllChanged(e) {
      this.selectedAttributes = e ? this.getParsedAttributes() : [];
    },
    checkAttribute(e, attr) {
      this.selectedAttributes = e
        ? [...this.selectedAttributes, attr]
        : [...this.selectedAttributes].filter(selAttr => selAttr.id !== attr.id);
    },
    async fillAttributes() {
      const usedDrivers = [...new Set(this.selectedAttributes.map(attr => attr.driver))];
      let errorState = false;
      usedDrivers.forEach(driver => {
        const driverMetadata = this.drivers.find(dr => dr.name === driver);
        if (driverMetadata.feature_limit && this.currentLayerSelectedFeatures.length > driverMetadata.feature_limit) {
          errorState = true;
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: this.$i18n.t('dialog.dataCalculator.limitExceeded', {
              limit: driverMetadata.feature_limit,
              driver: driverMetadata.verbose_name,
            }),
          });
        }
      });
      if (errorState) return;
      const payload = {
        features: this.currentLayerSelectedFeatures,
        attributes: this.selectedAttributes.map(attr => attr.name),
      };
      this.isDialogLoading = true;
      try {
        this.currentTaskId = await this.dataCalculatorCalculate({ datasource: this.currentDataSource, payload });
        this.$globalHub.$emit('handleCeleryTask', {
          currentTaskId: this.currentTaskId,
          taskSuccessResultParams: {
            type: 'data',
          },
          taskFailureSnackbar: {
            message: this.$i18n.t('dialog.dataCalculator.dataCalculatorGenericError'),
          },
          taskFailureCallback: this.resultCallback,
          taskSuccessCallback: response => {
            this.$store.set('snackbar/PUSH_MESSAGE!', {
              message: response,
            });
            this.$root.$emit('refreshTableData');
            this.$root.$emit('refreshMvtSource', this.currentLayerId);
            this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.currentLayerId] });
            this.$root.$emit('refreshFormData');
            this.computedIsVisible = false;
            this.resultCallback();
          },
        });
      } catch {
        this.resultCallback();
      }
    },
    resultCallback() {
      this.currentTaskId = null;
      this.isDialogLoading = false;
    },
    getParsedAttributes() {
      return this.currentDataSourceConfiguration.map(config => {
        const attrMetadata = this.dataSources[config.datasource]?.attributes_schema.attributes.find(
          attr => attr.id === config.attribute
        );
        return {
          id: config.id,
          name: attrMetadata?.name,
          verbose_name: attrMetadata?.verbose_name,
          driver: config.driver,
          driver_verbose_name: this.drivers.find(driver => driver.name === config.driver)?.verbose_name,
        };
      });
    },
    async init() {
      this.isFetched = false;
      try {
        await this.getDataCalculatorDrivers();
        this.attributes = this.getParsedAttributes();
      } finally {
        this.isFetched = true;
      }
    },
  },
  beforeDestroy() {
    if (this.currentTaskId) this.stopCeleryTask(this.currentTaskId);
  },
  mounted() {
    this.init();
  },
};
</script>
